import { useEffect, useState } from 'react';
import { api, formatPKR } from '../api';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';

import { CHARITY_LABEL, CHARITY_SHARE_PERCENT } from '../utils/constants';

export default function ProfitDistribution() {
  const [settings, setSettings] = useState({ profitThreshold: 500000 });
  const [analytics, setAnalytics] = useState(null);
  const [threshold, setThreshold] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    const [s, a] = await Promise.all([api.getSettings(), api.getAnalytics()]);
    setSettings(s);
    setThreshold(String(s.profitThreshold));
    setAnalytics(a);
  };

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.updateSettings({ profitThreshold: Number(threshold) });
      setMessage('Profit distribution threshold updated');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!analytics) return <p className="text-lumora-text/50">Loading...</p>;

  return (
    <div>
      <PageHeader
        title="Profit Distribution"
        subtitle="32% per owner · 4% charity (In The Name Of Allah)"
      />
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Profit" value={formatPKR(analytics.totalProfit)} accent />
        <StatCard label="Retained in Business" value={formatPKR(analytics.retainedProfit)} />
        <StatCard label="Distributable Profit" value={formatPKR(analytics.distributableProfit)} accent />
        <StatCard
          label={`Charity (${CHARITY_SHARE_PERCENT}%)`}
          value={formatPKR(analytics.charityShare?.shareAmount ?? 0)}
        />
      </div>

      <div className="card max-w-lg mb-8">
        <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Distribution Threshold</h3>
        <p className="text-sm text-lumora-text/60 mb-4">
          If profit is at or below the threshold, money stays in the business. Only profit above the
          threshold is split: each owner receives 32%, and 4% is set aside for charity ({CHARITY_LABEL}).
        </p>
        <form onSubmit={handleSave} className="flex gap-3">
          <input
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className="input"
            placeholder="Threshold in PKR"
            required
          />
          <button type="submit" className="btn-primary whitespace-nowrap">
            Save
          </button>
        </form>
        <p className="text-xs text-lumora-text/50 mt-3">
          Current threshold: {formatPKR(settings.profitThreshold)}
        </p>
        {message && <p className="text-green-700 text-sm mt-2">{message}</p>}
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </div>

      <div className="card">
        <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Owner Share Preview</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Owner</th>
                <th>Share %</th>
                <th>Investment</th>
                <th>Calculated Share</th>
              </tr>
            </thead>
            <tbody>
              {(analytics.ownerShares || []).map((o) => (
                <tr key={o.name}>
                  <td>{o.name}</td>
                  <td>{o.sharePercent}%</td>
                  <td>{formatPKR(o.investmentAmount ?? 0)}</td>
                  <td className="text-lumora-wood font-medium">{formatPKR(o.shareAmount)}</td>
                </tr>
              ))}
              {analytics.charityShare && (
                <tr className="bg-lumora-cream/40">
                  <td>{analytics.charityShare.label} (Charity)</td>
                  <td>{analytics.charityShare.sharePercent}%</td>
                  <td>—</td>
                  <td className="text-lumora-wood font-medium">
                    {formatPKR(analytics.charityShare.shareAmount)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
